import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { PageSEO } from "@/components/PageSEO";
import { API } from "@/lib/constants";

/* Scheda iniziale di «Quanto Mettere da Parte». Si apre solo con il codice personale,
   non c'è nessuna password. Se il codice non esiste la pagina è identica a una 404. */

const GRUPPI = [
  [2, "Commercio all'ingrosso e al dettaglio (negozi, e-commerce)"],
  [8, "Attività professionali, tecniche, sanitarie, istruzione"],
  [5, "Costruzioni e attività immobiliari"],
  [7, "Alloggio e ristorazione"],
  [1, "Industrie alimentari e delle bevande"],
  [6, "Intermediari del commercio (agenti e rappresentanti)"],
  [3, "Commercio ambulante di alimentari e bevande"],
  [4, "Commercio ambulante di altri prodotti"],
  [9, "Altre attività (artigiani di servizi, informatica, trasporti…)"],
  ["", "Non lo so"],
];

const GESTIONI = [
  ["commercianti", "INPS Commercianti"],
  ["artigiani", "INPS Artigiani"],
  ["gestione_separata", "INPS Gestione separata"],
  ["cassa_professionale", "Cassa professionale"],
  ["non_lo_so", "Non lo so"],
];

const Campo = ({ label, children, nota }) => (
  <label className="block">
    <span className="text-[#64708A] text-xs tracking-wide">{label}</span>
    {children}
    {nota && <span className="block text-[#8E9AAE] text-xs mt-1">{nota}</span>}
  </label>
);

const inputCls = "w-full mt-1 bg-white border border-[#D9D2C5] rounded px-3 py-2.5 text-[#0D1527]";

const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

/* mese chiuso prima di oggi (gennaio -> dicembre dell'anno prima) */
const meseChiuso = new Date().getMonth() === 0 ? 12 : new Date().getMonth();

const QmdpSchedaPage = () => {
  const { codice } = useParams();
  const [stato, setStato] = useState("caricamento");
  const [nome, setNome] = useState("");
  const [f, setF] = useState({ gestione: "commercianti", aliquota: "15" });
  const [salvata, setSalvata] = useState(false);
  const [err, setErr] = useState(null);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/qmdp/${codice}`)
      .then(({ data }) => {
        setNome(data.nome || "");
        setF((p) => ({ ...p, ...(data.scheda || {}), aliquota: data.scheda?.aliquota_startup ? "5" : "15" }));
        setStato("ok");
      })
      .catch(() => setStato("non-trovata"));
  }, [codice]);

  const salva = async () => {
    setErr(null);
    try {
      await axios.post(`${API}/qmdp/${codice}/scheda`, {
        ...f,
        gruppo_id: f.gruppo_id || null,
        incassato_fino_a_mese: f.incassato_fino_a_mese || meseChiuso,
        incassato_fino_a_anno: f.incassato_fino_a_anno || new Date().getFullYear(),
        aliquota_startup: f.aliquota === "5",
        aliquota_non_indicata: f.aliquota === "non_lo_so",
      });
      setSalvata(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErr(e.response?.data?.detail || "Non siamo riusciti a salvare: riprova tra poco.");
    }
  };

  if (stato === "caricamento") return <main className="pt-32 pb-20 min-h-screen bg-[#FAF8F5]" />;

  if (stato === "non-trovata") {
    return (
      <main className="pt-32 pb-24 min-h-screen bg-[#FAF8F5]" data-testid="qmdp-scheda-non-trovata">
        <PageSEO title="Pagina non trovata | SC Dottori Commercialisti" description="Pagina non trovata." noindex />
        <div className="max-w-xl mx-auto px-6 text-center">
          <h1 className="font-heading text-4xl text-[#0D1527] mb-4">PAGINA NON TROVATA</h1>
          <p className="text-[#4A5568]">
            Il link potrebbe essere incompleto. Apri il link dall'ultima email che ti abbiamo mandato oppure scrivi a{" "}
            <a className="text-[#8A6A28] underline" href="mailto:assistenza@casalisilvia.com">assistenza@casalisilvia.com</a>.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-32 pb-24 min-h-screen bg-[#FAF8F5]" data-testid="qmdp-scheda-page">
      <PageSEO title="La tua scheda | Quanto Mettere da Parte" description="Scheda personale del servizio Quanto Mettere da Parte." noindex />
      <div className="max-w-2xl mx-auto px-6">
        <p className="text-[#8A6A28] text-xs tracking-[0.3em] mb-4">QUANTO METTERE DA PARTE</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#0D1527] leading-tight">
          LA TUA <span className="text-[#2F7DBF]">PARTITA IVA</span>
        </h1>
        <p className="text-[#4A5568] mt-4">
          {nome ? `Ciao ${nome}, ` : ""}compila questa scheda una volta sola: ci serve per calcolare ogni mese quanto
          mettere da parte. Se non sai una risposta scegli «Non lo so»: controlliamo noi prima del primo calcolo.
        </p>

        {salvata && (
          <div className="mt-6 bg-[#E8F3FC] border border-[#3E8ED0] rounded p-4 text-[#1F4E75]" data-testid="qmdp-scheda-salvata">
            Scheda salvata. Il primo giorno del mese ti arriva la domanda sull'incasso.
          </div>
        )}

        <div className="mt-10 space-y-5">
          <Campo label="Che attività svolgi?">
            <select value={f.gruppo_id ?? ""} onChange={(e) => set("gruppo_id", e.target.value ? Number(e.target.value) : "")}
              className={inputCls} data-testid="scheda-attivita">
              {GRUPPI.map(([v, l]) => <option key={l} value={v}>{l}</option>)}
            </select>
          </Campo>
          {!f.gruppo_id && (
            <Campo label="Codice ATECO o descrizione dell'attività" nota="Lo troviamo noi: scrivi quello che sai.">
              <input value={f.codice_ateco || ""} onChange={(e) => set("codice_ateco", e.target.value)}
                className={inputCls} placeholder="es. 62.01 oppure «sviluppo siti web»" data-testid="scheda-ateco" />
            </Campo>
          )}
          <div className="grid sm:grid-cols-2 gap-5">
            <Campo label="Anno di inizio attività">
              <input type="number" value={f.anno_inizio || ""} onChange={(e) => set("anno_inizio", e.target.value)}
                className={inputCls} data-testid="scheda-anno-inizio" />
            </Campo>
            <Campo label="Aliquota dell'imposta">
              <select value={f.aliquota} onChange={(e) => set("aliquota", e.target.value)}
                className={inputCls} data-testid="scheda-aliquota">
                <option value="15">15%</option>
                <option value="5">5% (primi 5 anni)</option>
                <option value="non_lo_so">Non lo so</option>
              </select>
            </Campo>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Campo label="Dove versi i contributi?">
              <select value={f.gestione} onChange={(e) => set("gestione", e.target.value)}
                className={inputCls} data-testid="scheda-gestione">
                {GESTIONI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Campo>
            <Campo label="Hai la riduzione dei contributi?">
              <select value={f.riduzione_50 ? "50" : f.riduzione_35 ? "35" : "no"}
                onChange={(e) => { set("riduzione_35", e.target.value === "35"); set("riduzione_50", e.target.value === "50"); }}
                className={inputCls} data-testid="scheda-riduzione">
                <option value="no">No</option>
                <option value="35">Sì, del 35%</option>
                <option value="50">Sì, del 50%</option>
              </select>
            </Campo>
          </div>

          <p className="text-[#8A6A28] text-xs tracking-[0.25em] pt-6">DALLA DICHIARAZIONE DELL'ANNO SCORSO</p>
          <div className="grid sm:grid-cols-2 gap-5">
            <Campo label="Imposta sostitutiva dell'anno scorso">
              <input type="number" value={f.imposta_anno_precedente ?? ""} onChange={(e) => set("imposta_anno_precedente", e.target.value)}
                className={inputCls} data-testid="scheda-imposta-scorso" />
            </Campo>
            <Campo label="Contributi oltre il minimo dell'anno scorso">
              <input type="number" value={f.contributi_percentuale_anno_precedente ?? ""} onChange={(e) => set("contributi_percentuale_anno_precedente", e.target.value)}
                className={inputCls} data-testid="scheda-contributi-scorso" />
            </Campo>
          </div>

          <p className="text-[#8A6A28] text-xs tracking-[0.25em] pt-6">QUEST'ANNO FINORA</p>
          <div className="grid sm:grid-cols-[1fr_auto] gap-5 items-end">
            <Campo label={`Incassato da gennaio a ${MESI[(f.incassato_fino_a_mese || meseChiuso) - 1]}`}
              nota="Questo importo vale fino al mese indicato qui accanto: i mesi che dichiarerai dopo non vengono sommati di nuovo.">
              <input type="number" value={f.incassato_anno_precedenti_mesi ?? ""} onChange={(e) => set("incassato_anno_precedenti_mesi", e.target.value)}
                className={inputCls} data-testid="scheda-incassato" />
            </Campo>
            <Campo label="fino a tutto il mese di">
              <select value={f.incassato_fino_a_mese || meseChiuso}
                onChange={(e) => set("incassato_fino_a_mese", Number(e.target.value))}
                className={inputCls} data-testid="scheda-incassato-fino-a">
                {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </Campo>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Campo label="Imposta già pagata quest'anno (acconti)">
              <input type="number" value={f.acconti_imposta_pagati_anno ?? ""} onChange={(e) => set("acconti_imposta_pagati_anno", e.target.value)}
                className={inputCls} data-testid="scheda-acconti" />
            </Campo>
            <Campo label="Contributi già pagati quest'anno">
              <input type="number" value={f.contributi_pagati_anno ?? ""} onChange={(e) => set("contributi_pagati_anno", e.target.value)}
                className={inputCls} data-testid="scheda-contributi-pagati" />
            </Campo>
          </div>
          <Campo label="Di questi, quanto si riferisce all'anno prima?" nota="Di solito è la rata INPS pagata a febbraio.">
            <input type="number" value={f.contributi_pagati_competenza_anno_precedente ?? ""}
              onChange={(e) => set("contributi_pagati_competenza_anno_precedente", e.target.value)}
              className={inputCls} data-testid="scheda-contributi-anno-prima" />
          </Campo>
        </div>

        {err && <p className="text-red-600 text-sm mt-4" data-testid="qmdp-scheda-errore">{err}</p>}
        <button onClick={salva} data-testid="scheda-salva"
          className="mt-8 px-8 py-3.5 rounded bg-[#2F7DBF] text-white font-bold tracking-wide hover:bg-[#1F4E75] transition-colors">
          SALVA LA SCHEDA
        </button>
      </div>
    </main>
  );
};

export default QmdpSchedaPage;
